'use client';

import { countryAreaFromMelissa } from '@/lib/address/countryAreaFromMelissa';
import { getBlockedAddressErrorCode } from '@/lib/address/blockedAddresses';
import type {
  AddressValidationResult,
  MelissaValidationResult,
  ParsedAddress,
} from '@/lib/address/types';
import { MELISSA_RESULT_CODES } from '@/lib/address/types';
import { getShippingAddressErrorMessage } from '@/lib/checkout/errors/shippingAddressErrorMessages';
import { devError } from '@/lib/utils/devLog';
import { useLocale } from 'next-intl';
import { useCallback, useState } from 'react';

const API_URL = '/api/address/validate';

interface UseAddressValidationResult {
  validate: (address: ParsedAddress) => Promise<AddressValidationResult>;
  isValidating: boolean;
  lastResult: AddressValidationResult | null;
}

/**
 * Build streetAddress1 from Melissa validation response
 * Concatenates AddressLine1~3 until we hit city/postal
 */
function buildStreetAddress(melissa: MelissaValidationResult): string {
  const city = (melissa.Locality || '').toLowerCase();
  const postal = (melissa.PostalCode || '').toLowerCase().replace(/\s+/g, '');

  const parts: string[] = melissa.AddressLine1 ? [melissa.AddressLine1] : [];
  const extraLines = [melissa.AddressLine2, melissa.AddressLine3].filter(Boolean);

  for (const line of extraLines) {
    const normalized = line.toLowerCase().replace(/\s+/g, '');
    if ((city && normalized.includes(city)) || (postal && normalized.includes(postal))) {
      break;
    }
    parts.push(line);
  }

  return parts.join(', ');
}

/**
 * Parse Melissa result codes to determine validation status
 */
function parseResultCodes(results: string): { isValid: boolean; codes: string[] } {
  const codes = results.split(',').map((c) => c.trim());

  // Check for error codes
  const hasError = codes.some(
    (code) =>
      code === MELISSA_RESULT_CODES.GENERAL_ERROR ||
      code === MELISSA_RESULT_CODES.UNKNOWN_STREET ||
      code === MELISSA_RESULT_CODES.NON_DELIVERABLE_ADDRESS ||
      code === MELISSA_RESULT_CODES.MISSING_MINIMUM_ADDRESS
  );

  // Check for verified status
  const isVerified = codes.some((code) => code.startsWith(MELISSA_RESULT_CODES.VERIFIED));

  return {
    isValid: isVerified && !hasError,
    codes,
  };
}

/**
 * Map result codes to user-friendly error messages
 */
function getErrorMessages(codes: string[]): { field: string; message: string }[] {
  const errors: { field: string; message: string }[] = [];

  for (const code of codes) {
    switch (code) {
      case MELISSA_RESULT_CODES.GENERAL_ERROR:
        errors.push({ field: 'general', message: 'Unable to verify this address' });
        break;
      case MELISSA_RESULT_CODES.UNKNOWN_STREET:
        errors.push({ field: 'streetAddress1', message: 'Street address not found' });
        break;
      case MELISSA_RESULT_CODES.NON_DELIVERABLE_ADDRESS:
        errors.push({ field: 'general', message: 'This address is not deliverable' });
        break;
      case MELISSA_RESULT_CODES.MISSING_MINIMUM_ADDRESS:
        errors.push({ field: 'general', message: 'Please provide more address details' });
        break;
      case MELISSA_RESULT_CODES.LOCALITY_PARTIAL:
        errors.push({ field: 'city', message: 'City could not be fully verified' });
        break;
    }
  }

  return errors;
}

/**
 * Hook for validating addresses using Melissa API
 *
 * Features:
 * - Validates addresses against Melissa Global Address API
 * - Returns corrected/normalized addresses when available
 * - Checks for blocked address patterns
 * - Maps error codes to user-friendly messages
 *
 * @example
 * ```tsx
 * const { validate, isValidating, lastResult } = useAddressValidation();
 *
 * const result = await validate({
 *   streetAddress1: '123 Main St',
 *   city: 'New York',
 *   postalCode: '10001',
 *   country: 'US'
 * });
 *
 * if (!result.isValid) {
 *   console.log(result.errors);
 * }
 * ```
 */
export function useAddressValidation(): UseAddressValidationResult {
  const locale = useLocale();
  const [isValidating, setIsValidating] = useState(false);
  const [lastResult, setLastResult] = useState<AddressValidationResult | null>(null);

  const validate = useCallback(
    async (address: ParsedAddress): Promise<AddressValidationResult> => {
      const blockedCode = getBlockedAddressErrorCode(address);
      if (blockedCode) {
        const result: AddressValidationResult = {
          isValid: false,
          resultCodes: ['BLOCKED'],
          errors: [
            {
              field: 'general',
              code: blockedCode,
              message: getShippingAddressErrorMessage(blockedCode, locale),
            },
          ],
        };
        setLastResult(result);
        return result;
      }

      setIsValidating(true);

      try {
        const params = new URLSearchParams({
          a1: address.streetAddress1 || '',
          a2: address.streetAddress2 || '',
          loc: address.city || '',
          admarea: address.countryArea || '',
          postal: address.postalCode || '',
          ctry: address.country || 'US',
        });

        const response = await fetch(`${API_URL}?${params}`);

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Validation service unavailable');
        }

        const json = await response.json();
        // API returns { success: true, data: { Records: [...] } }
        const data = json.data || json;
        const record: MelissaValidationResult | undefined = data.Records?.[0];

        if (!record) {
          const result: AddressValidationResult = {
            isValid: false,
            resultCodes: ['NO_RESULT'],
            errors: [{ field: 'general', message: 'Unable to validate address' }],
          };
          setLastResult(result);
          return result;
        }

        const { isValid, codes } = parseResultCodes(record.Results);
        const errors = isValid ? [] : getErrorMessages(codes);

        // Build corrected address from Melissa response
        const correctedAddress: ParsedAddress = {
          streetAddress1: buildStreetAddress(record),
          city: record.Locality || address.city,
          postalCode: record.PostalCode || address.postalCode,
          countryArea: countryAreaFromMelissa(address.country, record, address.countryArea),
          country: address.country,
        };

        const result: AddressValidationResult = {
          isValid,
          resultCodes: codes,
          errors,
          correctedAddress,
        };

        setLastResult(result);
        return result;
      } catch (err) {
        devError('Address validation error:', err);

        // On service error, mark as invalid so unverified addresses don't slip through
        // User can retry or correct their address
        const result: AddressValidationResult = {
          isValid: false,
          resultCodes: ['SERVICE_ERROR'],
          errors: [
            {
              field: 'general',
              message: 'Unable to verify your address. Please check your details and try again.',
            },
          ],
        };
        setLastResult(result);
        return result;
      } finally {
        setIsValidating(false);
      }
    },
    [locale]
  );

  return {
    validate,
    isValidating,
    lastResult,
  };
}
